import { Component, Input, OnInit } from '@angular/core';
import {
  KpiTypes,
  PestTotalCallsByTypeData,
  ServiceMetricTypes,
  TotalCallsInsightModel,
} from '../../types/service-insight-types';
import { ServiceInsightsRequests } from 'src/app/core/services/service-insights-requests';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { ServiceCustomerFilterOptions } from 'src/app/global/service-customer-model';

/**
 * Component responsible for displaying a KPI bar in the service insights overview.
 * It handles the display of KPI names, units, and totals, and manages the interaction
 * with the service insights service for adding/removing metrics from the presentation.
 * The KPI includes the metric total value as well as any specific metric data relevant to the service/finding/calls performed.
 */
@Component({
    selector: 'app-service-insights-kpi-bar',
    templateUrl: './service-insights-kpi-bar.component.html',
    styleUrls: ['./service-insights-kpi-bar.component.scss'],
    standalone: false
})
export class ServiceInsightsKpiBarComponent implements OnInit {
  // Use KPI type to trigger corresponding request to the API
  @Input() serviceType?: ServiceMetricTypes;
  @Input() kpiName!: KpiTypes;

  displayKpiName: string = '';
  displayKpiUnits: string = '';
  kpiTotal: number = 0;
  metricTotal: number = 0;
  metricTotalDisplayUnits: string = '';
  metricData: TotalCallsInsightModel[] | undefined;
  loader: boolean = false;
  kpiChecked: boolean = false;
  metricTotalChecked: boolean = true;
  isPestCatchesSelected: boolean = false;

  ServiceMetricTypes = ServiceMetricTypes;

  constructor(
    private _serviceInsightsRequests: ServiceInsightsRequests,
    private _serviceInsightsService: ServiceInsightsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this._serviceInsightsService.isPestCatchesSelected$.subscribe(
      (isSelected) => {
        this.isPestCatchesSelected = isSelected;
      },
    );

    this._serviceInsightsService.isAllSelected.subscribe((allSelected) => {
      // Keep ScanCount (Pest Catches) unchecked by default. This will automatically uncheck the Add All checkbox.
      // Once user checks the All checkbox, we will check the Pest Catches checkbox as well
      // and add it to presentation. If user uncheck the All checkbox, we will uncheck the Pest Catches checkbox as well and remove it from presentation.
      if (this.kpiName === KpiTypes.ScanCountKPI) {
        this.kpiChecked = this.isPestCatchesSelected;
        this._onKpiCheckedChange(this.kpiChecked);
      } else {
        this.kpiChecked = allSelected;
        if (this.kpiName != 'CallsByReason') {
          this._onKpiCheckedChange(this.kpiChecked);
        }
      }
      if (this.serviceType) {
        this._onMetricTotalCheckedChange(allSelected); // Update the metric total checked state based on the "all selected" status
      }
    });

    // Decide how to display the kpiName and metricName
    [this.displayKpiName, this.displayKpiUnits] = this.getKpiDisplayName(
      this.kpiName,
    );

    this.metricTotalDisplayUnits = this.getMetricDisplayName(
      this.serviceType
    );

    this.loader = true;
    try {
      await this.populateKpiData();
      if (this.serviceType) {
        this.subscribeToPestSectionData();
        await this.loadPestMetricTotalData();
        this._onMetricTotalCheckedChange(this.metricTotalChecked);
      }
    } catch (error) {
      console.error('Error populating KPI data:', error);
    } finally {
      this.loader = false;
    }
  }

  private getKpiDisplayName(kpiName: KpiTypes): [string, string] {
    switch (kpiName) {
      case KpiTypes.ServiceKPI:
        return [`Service KPI's`, 'Locations Serviced'];
      case KpiTypes.CallsByReasonKPI:
        return [`ESR KPI's`, '% ESRs Closed'];
      case KpiTypes.ScanCountKPI:
        return [``, 'Total Scan Pest Count'];
      default:
        return [kpiName, ''];
    }
  }

  private getMetricDisplayName(serviceMetricType: ServiceMetricTypes | undefined) {
    switch(serviceMetricType) {
      case ServiceMetricTypes.Services:
        return 'Total Services';
      case ServiceMetricTypes.Findings:
        return 'Total Findings';
      case ServiceMetricTypes.CallsByReason:
        return 'Total Calls';
      default:
        return '';
    }
  }

  /**
   * Fetch and populate the KPI data based on the KPI type.
   * TODO: Migrate to a 'PestServiceInsightsStore' for managing KPI data.
   */
  private async populateKpiData() {
    try {
      switch (this.kpiName) {
        case KpiTypes.ServiceKPI:
          const serviceResponse =
            await this._serviceInsightsRequests.getPestKPITotalLocationsServiced();
          this.kpiTotal = serviceResponse.data['totalCount'];
          this._serviceInsightsService.addMetricToPresentation(
            this.displayKpiUnits,
            this.kpiTotal,
          );
          break;
        case KpiTypes.CallsByReasonKPI:
          const callsResponse =
            await this._serviceInsightsRequests.getPestKPITotalCallsClosed();
          this.kpiTotal = callsResponse.data['percentOfCallsClosed'];
          break;
        case KpiTypes.ScanCountKPI:
          const scanCountResponse =
            await this._serviceInsightsRequests.getTotalScanPestCountKPI();
          this.kpiTotal = scanCountResponse.data['totalCount'];
          // Do NOT add Scan Pest Count to presentation by default; leave unchecked
          break;
      }
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    }
  }

  /**
   * Subscribes to the pest section data for the current service type and updates the metric data and total accordingly.
   * TODO: Migrate to a 'PestServiceInsightsStore' for managing service data. Might also be worth a shot to consolidate all the
   * requests for a single service type in the API.
   */
  private subscribeToPestSectionData() {
    this._serviceInsightsService
      .getPestTotalCallsByTypeData(
        `${this.serviceType}-total-calls` as keyof PestTotalCallsByTypeData,
      )
      .subscribe((f) => {
        this.metricData = f.totalCalls;
        this.metricTotal =
          this.metricData?.reduce((sum, curr) => sum + curr.total, 0) ?? 0;
      });
  }

  private async loadPestMetricTotalData() {
    // Seed empty advanced filters
    const advancedFilters = this._serviceInsightsService.serviceCustomerFilters.getValue();
    if (!this.metricData || this.metricData.length === 0) {
      this._serviceInsightsService.setPestTotalCallsByTypeData(
        `${this.serviceType}-total-calls` as keyof PestTotalCallsByTypeData,
        await this._serviceInsightsService.getTotalCallsByTypeFromAPI(
          this.serviceType!,
          advancedFilters,
        ),
      );
    }
  }

  /**
   * Adds or removes the KPI metric from the presentation based on the checkbox state.
   * @param checked The state of the checkbox indicating whether the metric total should be added or removed from the presentation.
   */
  _onKpiCheckedChange(checked: boolean) {
    this.kpiChecked = checked;
    //Check the display unit for Total Scan Pest Count then change the metric name to Pest Catches before sending to presentation, otherwise send the display unit as is.
    const metricName =
      this.displayKpiUnits === 'Total Scan Pest Count'
        ? 'Pest Catches'
        : this.displayKpiUnits;
    this._serviceInsightsService.setPestMetricsSelected(
      metricName,
      this.kpiChecked
    );
    if (this.kpiChecked && this.kpiTotal > 0) {
      this._serviceInsightsService.addMetricToPresentation(
        metricName,
        this.kpiTotal
      );
    } else {
      this._serviceInsightsService.removeMetricFromPresentation(metricName);
    }

  }

  /**
   * Add or removes the metric total from the presentation based on the checkbox state.
   * Currently only applicable for the Services metric total. Other metric totals are added by default
   * @param checked The state of the checkbox indicating whether the metric total should be added or removed from the presentation.
   */
  _onMetricTotalCheckedChange(checked: boolean) {
    const presentationName =
      this.serviceType === ServiceMetricTypes.Services
        ? 'Service Visits'
        : `Total ${this.serviceType}`;
    this.metricTotalChecked = checked;
    if (
      this.metricTotalChecked &&
      this.metricTotal > 0 &&
      this.serviceType !== ServiceMetricTypes.Findings &&
      this.serviceType !== ServiceMetricTypes.CallsByReason
    ) {
      this._serviceInsightsService.addMetricToPresentation(
        presentationName,
        this.metricTotal
      );
    } else {
      this._serviceInsightsService.removeMetricFromPresentation(
        presentationName
      );
    }
    this._serviceInsightsService.setPestMetricsSelected(
      presentationName,
      this.metricTotalChecked
    );
  }
}
